<template>
  <ion-page>
    <main-header :back-href="editLocationHref"></main-header>
    <ion-content padding>
      <loading-spinner v-if="loading"></loading-spinner>
      <div v-else>
        <div
          v-if="!location"
          class="background-color ion-color-danger ion-padding ion-text-center"
        >
          The location was not found
        </div>
        <div
          v-else-if="!menu"
          class="background-color ion-color-danger ion-padding ion-text-center"
        >
          The menu was not found
        </div>
        <div v-else>
          <h2 class="ion-no-margin-top ion-margin-bottom">
            Menu for {{ location.name }}
          </h2>

          <div
            v-if="
              (!Array.isArray(this.menu.categories) ||
                this.menu.categories.length == 0) &&
                !addingCategory
            "
            class="background-color ion-color-warning ion-padding ion-text-center"
          >
            To get started, add the first category.
          </div>

          <ion-list>
            <template v-for="(category, index) in menu.categories">
              <menu-category
                :key="`category-${category.vueId}`"
                :value="menu.categories[index]"
                :up-disabled="index == 0"
                :down-disabled="index == menu.categories.length - 1"
                @save="
                  menu.categories[index] = $event;
                  save();
                "
                @delete="deleteMenuCategory(category.vueId)"
                @up="moveCategoryUp(index)"
                @down="moveCategoryDown(index)"
              ></menu-category>
            </template>
            <menu-category
              v-if="addingCategory"
              @delete="addingCategory = false"
              :new-category="true"
              @save="addNewCategory"
              :up-disabled="true"
              :down-disabled="true"
            >
            </menu-category>
          </ion-list>

          <hr />

          <div>
            <ion-button
              v-if="!addingCategory"
              color="light"
              @click="addingCategory = true"
            >
              Add Category
            </ion-button>
          </div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
/**
 * This component was made when time was very limited. Please do not duplicate these methodologies
 * for other features please.
 *
 * Since menus will probably not be edited as often, or definitely not as much as say - ordering - this
 * data transfer is less efficient - only to try to get this finished in the time frame. In the
 * future, it may be a great idea to issue patches instead of full updates (faster and cheaper).
 *
 * Additionally, it would be a better idea to break out some of the forms into other components most likely.
 */
import { mapGetters } from "vuex";
import Location from "@/models/Location";
import Menu from "@/models/Menu";
import MenuCategory from "@/components/Web/MenuCategory";
import errorAlert from "../../../../ErrorAlert";

export default {
  title: "Edit Menu",

  components: {
    "menu-category": MenuCategory
  },

  data: function() {
    return {
      loading: true,
      location: null,
      menu: null,

      addingCategory: false,
      categoryName: "",
      vueIdIncrementer: 0
    };
  },

  created() {
    if (!this.userIsAdmin && !this.userIsVendor) {
      return this.$router.push("/").catch(e => e);
    }

    Promise.all(
      [
        this.$firestore()
          .collection("locations")
          .withConverter(Location.firebaseConverter)
          .doc(this.$route.params.id)
          .get()
          .then(doc => {
            this.location = doc.data();
          })
      ],
      [
        this.$firestore()
          .collection("menus")
          .withConverter(Menu.firebaseConverter)
          .doc(this.$route.params.id)
          .get()
          .then(doc => {
            this.menu = doc.data();

            // now we have to generate IDs so vue can do its thing
            if (this.menu && Array.isArray(this.menu.categories)) {
              this.menu.categories = this.menu.categories.map(category => {
                this.vueIdIncrementer = this.vueIdIncrementer + 1;
                return { vueId: this.vueIdIncrementer, ...category };
              });
            }
          })
      ]
    ).then(() => {
      this.loading = false;
    });
  },

  methods: {
    deleteMenuCategory(vueId) {
      this.menu.categories.splice(
        this.menu.categories.findIndex(i => i.vueId == vueId),
        1
      );
      this.save();
    },

    moveCategoryUp(index) {
      // this shouldn't happen
      if (index == 0) {
        return errorAlert("Unable to move.");
      }

      const swapIndex = index - 1;
      let replace = [
        this.menu.categories[index],
        this.menu.categories[swapIndex]
      ];
      this.menu.categories.splice(swapIndex, 2, replace[0], replace[1]);
      this.save();
    },

    moveCategoryDown(index) {
      // this shouldn't happen ever
      if (index == this.menu.categories.length - 1) {
        return errorAlert("Unable to move.");
      }

      const swapIndex = index + 1;
      let replace = [
        this.menu.categories[swapIndex],
        this.menu.categories[index]
      ];
      this.menu.categories.splice(index, 2, replace[0], replace[1]);
      this.save();
    },

    addNewCategory(category) {
      this.vueIdIncrementer = this.vueIdIncrementer + 1;
      const vueId = this.vueIdIncrementer;
      if (!this.menu.categories) {
        this.menu.categories = [];
      }
      this.menu.categories.push({
        vueId,
        ...category
      });
      this.addingCategory = false;
      this.save();
    },

    save() {
      this.$firestore()
        .collection("menus")
        .doc(this.menu.id)
        .update({
          categories: this.menu.categories.map(category => {
            // eslint-disable-next-line
            let { vueId, ...cleaned } = category;
            return cleaned;
          }),
          updated: this.$firestore.FieldValue.serverTimestamp()
        })
        .catch(e => {
          errorAlert(e.message);
        });
    }
  },

  computed: {
    ...mapGetters("auth", ["userIsAdmin", "userIsVendor"]),

    editLocationHref() {
      if (this.userIsAdmin) {
        return this.$router.resolve({
          name: "Web: Location Edit",
          params: {
            id: this.$route.params.id
          }
        }).href;
      } else {
        return "/";
      }
    }
  }
};
</script>

<style lang="scss">
.menu-category {
  --background: #f4f4f4;

  ion-label.hydrated,
  ion-input {
    font-size: 1.4rem;
  }

  ion-label.hydrated {
    max-width: initial;
  }
}
</style>
